import {ChevronRight, PersonOutlineOutlined, Search} from '@mui/icons-material';
import {usePersona} from 'src/permissions/hooks';
import {Box, OutlinedInput, Typography} from '@mui/material';
import {FC, useEffect, useMemo, useRef, useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {useNavigate} from 'react-router-dom';
import {useGetAccessSummaryQuery, useGetPermissionCatalogQuery, useGetRolesQuery, usersApi} from 'src/api';
import {UserTypeEnum} from 'src/enums/common';
import {BaseUserInfo} from 'src/types';
import {searchInputSx, toolbarActionsSx} from 'src/components/tables/tableToolbar.sx';
import {ToolbarSearchSelect} from 'src/components/common/ToolbarSearchSelect';
import {AppRouteEnum, AppRouteSpace} from 'src/utils/helper';
import {getEvDriverNameFromBaseUser} from 'src/utils/userNameFormatter';
import {cardSx, emptySx, listFooterSx, listScrollSx, sectionHintSx} from 'src/pages/AccessControl/AccessControl.sx';
import {TablePagination} from 'src/components/tables/TablePagination';
import {TableRowCounter} from 'src/components/tables/TableRowCounter';
import {
    IAM_TONE,
    columnHeadSx,
    countColSx,
    listHeaderRowSx,
    listRowSx,
    nameColSx,
    pillSx,
    scopeColSx,
    subjectBadgeSx,
} from '../iam.sx';
import theme from 'src/theme';
import {useBusinessId} from 'src/app/hooks';

/** Long enough that a keystroke does not cost a request, short enough to feel immediate. */
const SEARCH_DELAY_MS = 350;
const DEFAULT_PAGE_SIZE = 25;

/**
 * Who has access, and to what.
 *
 * The list is the user directory; the access itself lives on the single page behind each row. Searching
 * runs on the server because a business can have thousands of drivers, and the interesting ones here are
 * usually staff — someone arriving at this screen is looking for a particular person, not browsing.
 */
/**
 * Which kinds of account reach how far.
 *
 * The server decides a person's scope from their account type - one ceiling per type, in
 * user_type_scope_ceiling - so filtering by scope is filtering by the types that reach it, and the
 * search needs no new field. Kept here as the one place that mirrors that table; if a ceiling moves,
 * this is what moves with it.
 */
const TYPES_BY_SCOPE: Record<string, string[]> = {
    GLOBAL: [UserTypeEnum.SUPER_ADMIN],
    BUSINESS: [
        UserTypeEnum.BUSINESS,
        UserTypeEnum.CLIENT_MANAGER,
        UserTypeEnum.SALES_MANAGER,
        UserTypeEnum.TECHNICAL_MANAGER,
    ],
    REAL_ESTATE: [UserTypeEnum.REAL_ESTATE],
    COMPANY: [UserTypeEnum.COMPANY],
    // An employee was capped at COMPANY until V1.161.0 lowered them to SELF.
    SELF: [UserTypeEnum.EMPLOYEE, UserTypeEnum.PRIVATE_DRIVER, UserTypeEnum.UNDEFINED],
};

export const UsersSection: FC = () => {
    const persona = usePersona();
    const intl = useIntl();
    const navigate = useNavigate();


    const [keyword, setKeyword] = useState('');
    const [scope, setScope] = useState('');
    const [roleId, setRoleId] = useState('');
    const [permission, setPermission] = useState('');
    const [debounced, setDebounced] = useState('');
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(DEFAULT_PAGE_SIZE);
    const [fetchUsers, {data, isFetching}] = usersApi.useLazyGetUsersQuery();
    // The business being worked in. A platform-wide caller is clamped by nothing, so without this
    // the directory listed every tenant's people and went on listing them after the switch.
    const businessId = useBusinessId();

    useEffect(() => {
        const timer = setTimeout(() => setDebounced(keyword.trim()), SEARCH_DELAY_MS);
        return () => clearTimeout(timer);
    }, [keyword]);

    // A new search means a new result set, so page 1 of it. Keeping the old page number here shows an
    // empty card whenever the narrower search has fewer pages than the wider one.
    useEffect(() => {
        setPage(0);
    }, [debounced, scope, roleId, permission, businessId]);

    useEffect(() => {
        void fetchUsers({
            data: {
                businessId,
                ...(debounced ? {keyword: debounced} : {}),
                ...(scope ? {userTypes: TYPES_BY_SCOPE[scope]} : {}),
                ...(roleId ? {roleId: Number(roleId)} : {}),
                ...(permission ? {permission} : {}),
            },
            params: {page, size},
        });
    }, [debounced, scope, roleId, permission, page, size, businessId]);

    const users = useMemo(() => data?.content ?? [], [data]);

    // A new page starts at its first row, as in every other list - the scroller is replaced in place.
    const scrollRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        scrollRef.current?.scrollTo({top: 0});
    }, [page]);

    // The two axes this screen is actually browsed by: what kind of account somebody holds, and which
    // role they were given. Roles come from the server because a business names its own, and a list
    // written here would go stale the first time somebody added one.
    const {data: roles} = useGetRolesQuery(businessId);
    // The catalog, sorted the way it reads rather than the way it is stored: a hundred and fifty codes
    // in insertion order is a list nobody can find anything in.
    const {data: catalog} = useGetPermissionCatalogQuery();
    const permissionOptions = useMemo(
        () => [...(catalog ?? [])]
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((item) => ({value: item.name, label: item.name, hint: item.description})),
        [catalog]
    );
    // Every scope except the platform's, which only the platform administrator ever sees: a business
    // reaches its own people, and none of them is global, so offering the option promised a filter that
    // could only ever come back empty.
    const scopeOptions = useMemo(
        () => Object.keys(TYPES_BY_SCOPE)
            .filter((candidate) => candidate !== 'GLOBAL' || persona.isPlatformAdmin)
            .map((candidate) => ({
                value: candidate,
                label: intl.formatMessage({
                    id: `accessControlScope.${candidate}`,
                    defaultMessage: candidate,
                }),
            })),
        [intl, persona.isPlatformAdmin]
    );
    const roleOptions = useMemo(
        () => (roles ?? []).map((role) => ({value: String(role.id), label: role.name})),
        [roles]
    );

    // Asked for by id, for the page already on screen: the list is paged and filtered by the user
    // search, and searching a second time here would be a second chance for the two to disagree about
    // who the caller may see.
    const ids = useMemo(() => users.map((user) => user.id), [users]);
    const {data: access} = useGetAccessSummaryQuery(ids, {skip: ids.length === 0});
    const accessById = useMemo(
        () => new Map((access ?? []).map((row) => [row.userId, row])),
        [access]
    );

    const open = (user: BaseUserInfo) =>
        navigate(
            AppRouteSpace([AppRouteEnum.app, AppRouteEnum.accessControl, AppRouteEnum.users, String(user.id)])
        );

    return (
        <Box>
            <Typography sx={sectionHintSx}>
                <FormattedMessage
                    id="accessControlUsersHint"
                    defaultMessage="Open a person to see the roles they hold, any permission granted or refused to them individually, and the full list of what they can do as a result. How far each of them sees is set by the kind of account, shown here."
                />
            </Typography>

            <Box sx={{...toolbarActionsSx, mb: 2.5, gap: 1.5, flexWrap: 'wrap'}}>
                <OutlinedInput
                    sx={searchInputSx}
                    color="secondary"
                    type="text"
                    name="search"
                    value={keyword}
                    onChange={(event) => setKeyword(event.target.value)}
                    placeholder={intl.formatMessage({id: 'search'})}
                    startAdornment={<Search/>}
                />
                {/* Typed into rather than scrolled: the permission catalogue alone is a hundred and
                    fifty entries, and a plain select opens all of them in one column you can only use
                    if you already know what is in it. Empty means "all", said inside the box. */}
                <ToolbarSearchSelect
                    value={scope}
                    onChange={setScope}
                    options={scopeOptions}
                    width={200}
                    emptyLabel={intl.formatMessage({
                        id: 'accessControlAllScopes',
                        defaultMessage: 'All scopes',
                    })}
                />
                <ToolbarSearchSelect
                    value={roleId}
                    onChange={setRoleId}
                    options={roleOptions}
                    width={220}
                    emptyLabel={intl.formatMessage({
                        id: 'accessControlAllRoles',
                        defaultMessage: 'All roles',
                    })}
                />
                {/* The question a role filter is usually standing in for. A role is how access is
                    normally given, but not the only way it arrives, and this answers "who can actually
                    do this" - including the person whose role grants it and whose own exception takes
                    it back. */}
                <ToolbarSearchSelect
                    value={permission}
                    onChange={setPermission}
                    options={permissionOptions}
                    width={260}
                    emptyLabel={intl.formatMessage({
                        id: 'accessControlAllPermissions',
                        defaultMessage: 'Any permission',
                    })}
                />
            </Box>

            <Box sx={cardSx}>
                {/* Named columns rather than a bare badge on each row. The badge said PRIVATE DRIVER
                    and nothing said what that was - a role, a status, a plan - which matters more now
                    that the kind of account is what decides how far somebody sees. */}
                <Box ref={scrollRef} sx={listScrollSx}>
                    {/* Inside the scrolling area and stuck to its top. Outside it the header kept the
                        card's full width while the rows lost the scrollbar's, and the flex column
                        between them absorbed the difference - so every column after the roles sat left
                        of the heading naming it, by however wide this browser draws a scrollbar. */}
                    <Box sx={listHeaderRowSx}>
                        {/* Stands in for the avatar badge on every row below. Without it each heading sat
                            one badge and one gap to the left of the column it names. */}
                        <Box sx={{width: 36, flexShrink: 0}}/>
                        <Typography sx={{...columnHeadSx, width: 300, flex: 'none'}}>
                            <FormattedMessage id="accessControlColumnPerson" defaultMessage="Person"/>
                        </Typography>
                        {/* The kind of account and how far it reaches read as one fact, because they are
                            one: the type is what decides the reach. Stacked like the name over the email
                            rather than spread across the row with a column of roles between them. */}
                        {/* Wider than the account type it replaced: "Their own data only" is half again
                            the length of "Employee", and the roles beside it were starting under the
                            tail of it. */}
                        <Typography sx={{...columnHeadSx, width: 200, flex: 'none'}}>
                            <FormattedMessage id="accessControlColumnScope" defaultMessage="Scope"/>
                        </Typography>
                        <Typography sx={columnHeadSx}>
                            <FormattedMessage id="accessControlColumnRoles" defaultMessage="Roles"/>
                        </Typography>
                        {/* A number rather than a list, and its own column rather than another pill among
                            the roles: it is the one value here worth comparing down the column, and pills
                            wrap, so it would sit in a different place on every row. */}
                        <Typography sx={{...columnHeadSx, width: 88, flex: 'none', mr: 3}}>
                            <FormattedMessage
                                id="accessControlColumnPermissions"
                                defaultMessage="Permissions"
                            />
                        </Typography>
                        {/* Keeps the heading aligned with the rows, which end in a chevron. */}
                        <Box sx={{width: 24}}/>
                    </Box>
                    {!isFetching && users.length === 0 && (
                        <Typography sx={emptySx}>
                            <FormattedMessage id="accessControlNoUsers" defaultMessage="No people found"/>
                        </Typography>
                    )}

                    {users.map((user) => (
                        <Box key={user.id} sx={{...listRowSx, cursor: 'pointer'}} onClick={() => open(user)}>
                            <Box sx={subjectBadgeSx}>
                                <PersonOutlineOutlined fontSize="small"/>
                            </Box>
                            {/* Wide enough for the email under the name. Set off with a margin
                                instead, the address still did not fit and only the gap grew - the
                                separation was never what was short. */}
                            <Box sx={nameColSx}>
                                <Typography noWrap sx={{fontWeight: 600, fontSize: '0.9375rem'}}>
                                    {getEvDriverNameFromBaseUser(user)}
                                </Typography>
                                <Typography
                                    noWrap
                                    sx={{fontSize: '0.8125rem', color: theme.palette.neutral[400]}}
                                >
                                    {user.emailAddress}
                                </Typography>
                            </Box>
                            {/* How far this person sees, which is the question this screen is about.
                                The kind of account decided it and is no longer shown for itself:
                                two columns saying one fact invited comparing people by the wrong one. */}
                            <Box sx={scopeColSx}>
                                {accessById.get(user.id)?.scopeType && (
                                    <Typography sx={pillSx(IAM_TONE.platform)}>
                                        {intl.formatMessage({
                                            id: `accessControlScope.${accessById.get(user.id)?.scopeType}`,
                                            defaultMessage: accessById.get(user.id)?.scopeType,
                                        })}
                                    </Typography>
                                )}
                            </Box>
                            {/* Named roles, and the exceptions counted beside them - a person with a
                                permission granted or refused individually is the one worth opening,
                                and their roles alone do not say so. */}
                            <Box
                                sx={{
                                    flex: 1,
                                    minWidth: 0,
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 0.5,
                                }}
                            >
                                {/* Nothing at all until this row's access has arrived. The roles come
                                    from a second request, and an absent answer is not the same claim
                                    as "no roles" - rendering the latter meant every row announced
                                    that nobody had any, for as long as the request took. */}
                                {(accessById.get(user.id)?.roleNames ?? []).map((role) => (
                                    <Typography key={role} sx={pillSx(IAM_TONE.yours)}>{role}</Typography>
                                ))}
                                {accessById.has(user.id)
                                    && !accessById.get(user.id)?.roleNames.length && (
                                    <Typography sx={pillSx(IAM_TONE.refused)}>
                                        <FormattedMessage
                                            id="accessControlNoRoles"
                                            defaultMessage="No roles"
                                        />
                                    </Typography>
                                )}
                                {/* Said in the words the person's page uses, and only when it changes
                                    something. "1 Exceptions" beside a page listing none was two
                                    questions being answered at once: how many rows exist, and what any
                                    of them do. */}
                                {!!accessById.get(user.id)?.givenExtraCount && (
                                    <Typography sx={pillSx(IAM_TONE.granted)}>
                                        {intl.formatMessage(
                                            {
                                                id: 'accessControlNGivenExtra',
                                                defaultMessage: '{count, plural, one {# given extra} '
                                                    + 'other {# given extra}}',
                                            },
                                            {count: accessById.get(user.id)?.givenExtraCount}
                                        )}
                                    </Typography>
                                )}
                                {!!accessById.get(user.id)?.takenAwayCount && (
                                    <Typography sx={pillSx(IAM_TONE.refused)}>
                                        {intl.formatMessage(
                                            {
                                                id: 'accessControlNTakenAway',
                                                defaultMessage: '{count, plural, one {# taken away} '
                                                    + 'other {# taken away}}',
                                            },
                                            {count: accessById.get(user.id)?.takenAwayCount}
                                        )}
                                    </Typography>
                                )}
                            </Box>
                            {/* The number their own page adds up to, not the sum of what their roles
                                list: a personal refusal subtracts and a ceiling can withhold, so the
                                two differ exactly for the people worth looking at. Blank rather than
                                zero until the row's access has arrived - nought is a claim, and it
                                would be the wrong one on every row for as long as the request takes. */}
                            <Typography sx={countColSx}>
                                {accessById.has(user.id) ? accessById.get(user.id)?.permissionCount : ''}
                            </Typography>
                            <ChevronRight sx={{color: 'grey.500'}}/>
                        </Box>
                    ))}
                </Box>
                {/* Kept for an empty result too: hiding it took the card's footer away and shortened
                    it again, and "1 of 1" over no rows is the honest answer rather than an absence. */}
                {!!data && (
                    <Box sx={listFooterSx}>
                        <TableRowCounter
                            defaultValue={size}
                            handleChangeRowsNumber={(event) => {
                                setSize(Number(event.target.value));
                                setPage(0);
                            }}
                        />
                        <TablePagination
                            totalPages={data.page.totalPages}
                            page={data.page.number}
                            handleChangePage={setPage}
                        />
                    </Box>
                )}
            </Box>
        </Box>
    );
};
